// MentionInput.jsx — textarea that autocompletes @user handles from a member list
import React, { useRef, useState } from 'react';
import { getAvatarUrl } from '../lib/api';

// Given text and caret position, find the current "@fragment" (returns { start, frag }) or null.
function currentMention(text, caret) {
  if (caret == null) return null;
  const upto = text.slice(0, caret);
  const at = upto.lastIndexOf('@');
  if (at < 0) return null;
  // must be at start OR preceded by space / newline
  if (at > 0 && !/\s/.test(upto[at - 1])) return null;
  const frag = upto.slice(at + 1);
  if (/\s/.test(frag)) return null;
  if (frag.length > 32) return null;
  return { start: at, frag };
}

function handleFor(user) {
  return (user.full_name || user.email || '').split(' ')[0].toLowerCase();
}

export default function MentionInput({
  value, onChange, members = [], placeholder, disabled, onBlur, className, minRows = 2, testId,
}) {
  const ref = useRef(null);
  const [caret, setCaret] = useState(null);
  const [selIdx, setSelIdx] = useState(0);

  const mention = currentMention(value || '', caret);
  const matches = mention
    ? members.filter((m) => {
        if (!mention.frag) return true;
        const first = (m.full_name || '').split(' ')[0].toLowerCase();
        const full = (m.full_name || '').toLowerCase();
        return first.startsWith(mention.frag.toLowerCase()) || full.includes(mention.frag.toLowerCase());
      }).slice(0, 6)
    : [];

  const insertHandle = (user) => {
    const h = handleFor(user);
    if (!mention) return;
    const before = value.slice(0, mention.start);
    const after = value.slice((caret ?? mention.start + 1 + mention.frag.length));
    const next = `${before}@${h} ${after}`;
    onChange(next);
    // Position cursor after inserted handle
    setTimeout(() => {
      if (ref.current) {
        const pos = before.length + 1 + h.length + 1;
        ref.current.selectionStart = ref.current.selectionEnd = pos;
        ref.current.focus();
      }
    }, 0);
    setSelIdx(0);
  };

  const onKeyDown = (e) => {
    if (!matches.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setSelIdx((i) => (i + 1) % matches.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setSelIdx((i) => (i - 1 + matches.length) % matches.length); }
    else if (e.key === 'Enter' || e.key === 'Tab') {
      if (mention) { e.preventDefault(); insertHandle(matches[selIdx]); }
    } else if (e.key === 'Escape') { setSelIdx(0); }
  };

  return (
    <div style={{ position: 'relative' }}>
      <textarea
        ref={ref}
        className={className || 'tm-input'}
        style={{ minHeight: minRows * 24 + 16, fontFamily: 'inherit', resize: 'vertical' }}
        value={value || ''}
        onChange={(e) => { onChange(e.target.value); setCaret(e.target.selectionStart); }}
        onKeyDown={onKeyDown}
        onKeyUp={(e) => setCaret(e.target.selectionStart)}
        onClick={(e) => setCaret(e.target.selectionStart)}
        onBlur={(e) => { setTimeout(() => setCaret(null), 150); onBlur?.(e); }}
        placeholder={placeholder}
        disabled={disabled}
        data-testid={testId}
      />
      {matches.length > 0 && mention && (
        <div className="tm-pop" data-testid={`${testId || 'mention'}-suggest`}
          style={{ position: 'absolute', top: '100%', left: 0, minWidth: 200, zIndex: 40, marginTop: 4 }}>
          <div style={{ fontSize: 10, color: 'var(--trello-text-muted)', textTransform: 'uppercase', letterSpacing: 0.4, padding: '2px 4px' }}>
            Mention someone
          </div>
          {matches.map((m, i) => (
            <button key={m.id} type="button" data-testid={`mention-opt-${m.id}`}
              onMouseDown={(e) => { e.preventDefault(); insertHandle(m); }}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%',
                textAlign: 'left', border: 'none', cursor: 'pointer',
                background: i === selIdx ? '#E4F0F6' : 'transparent',
                borderRadius: 4, padding: '6px 8px', fontSize: 13,
              }}>
              <div style={{
                width: 22, height: 22, borderRadius: '50%',
                background: m.avatar_url ? `url(${getAvatarUrl(m.avatar_url)}) center/cover` : (m.avatar_color || 'var(--trello-text-muted)'),
                color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 10, fontWeight: 700,
              }}>
                {!m.avatar_url && (m.full_name || '?').split(' ').map((s) => s[0]).join('').slice(0, 2).toUpperCase()}
              </div>
              <span>{m.full_name}</span>
              <span style={{ marginLeft: 'auto', color: 'var(--trello-text-muted)', fontSize: 11 }}>@{handleFor(m)}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Highlight @mentions in read-only text.
export function MentionText({ text }) {
  if (!text) return null;
  const parts = String(text).split(/(@[A-Za-z0-9._-]+)/g);
  return (
    <>
      {parts.map((p, i) => p.startsWith('@')
        ? <span key={i} style={{ background: 'var(--trello-list-bg)', color: '#026AA7', padding: '0 4px', borderRadius: 3, fontWeight: 600 }}>{p}</span>
        : <React.Fragment key={i}>{p}</React.Fragment>)}
    </>
  );
}
